import { inject, Injector } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import { signalStoreFeature, type, withMethods } from '@ngrx/signals'
import { Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events'
import { catchError, EMPTY, exhaustMap, filter, groupBy, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs'

import { isPaymentRequired } from '@app/core/is-payment-required'
import { nonCancelableRequest } from '@app/core/rxjs/non-cancelable-request'
import { CreditsStore } from '@app/modules/credits/store/credits.store'
import { conversationEvents } from '@app/modules/messaging/components/store/conversation.events'
import type { ConversationState } from '@app/modules/messaging/components/store/conversation.store'
import { CONVERSATION_STORE_CONFIG } from '@app/modules/messaging/components/store/conversation.store'
import { injectDefaultErrorHandler } from '@app/modules/messaging/components/store/default-error-handler'
import type { Conversation } from '@app/modules/messaging/models/conversation.model'
import type { Message } from '@app/modules/messaging/models/message.model'
import { ConversationUIService } from '@app/modules/messaging/services/conversation-ui.service'
import { ConversationService } from '@app/modules/messaging/services/conversation.service'
import { MessagingService } from '@app/modules/messaging/services/messaging.service'
import { CreditsNavigationService } from '@app/modules/navigation/services/credits-navigation.service'

import { messagePhotoEvents } from './message-photo.events'

export const messagePhotoFeature = () =>
  signalStoreFeature(
    { state: type<ConversationState>() },
    withMethods(
      (
        _store,
        conversationUIService = inject(ConversationUIService),
        creditsNavigationService = inject(CreditsNavigationService),
        messagingService = inject(MessagingService),
        source = inject(CONVERSATION_STORE_CONFIG).source,
        dispatch = injectDispatch(messagePhotoEvents),
      ) => ({
        attemptToUnlockMessagePhoto: (
          message: Message & { type: 'photo'; status: 'locked' },
          conversation: Conversation,
        ) => {
          if (!messagingService.canAffordMessagePhotoUnlock(conversation)) {
            const instance = conversationUIService.openMessagePhotoUnlockInsufficientCreditsModal()
            instance.onConfirm(() =>
              creditsNavigationService.visitCredits('unlockMessagePhoto', {
                source,
                profile_id: conversation.profile.id,
              }),
            )
            return
          }

          dispatch.unlock({ message, profile_id: conversation.profile.id })
        },

        viewMessagePhoto: (message: Message & { type: 'photo'; status: 'viewable' }) => {
          dispatch.view({ message })
        },
      }),
    ),

    withEventHandlers(
      (
        _store,
        conversationService = inject(ConversationService),
        conversationUIService = inject(ConversationUIService),
        creditsNavigationService = inject(CreditsNavigationService),
        creditsStore = inject(CreditsStore),
        injector = inject(Injector),
        source = inject(CONVERSATION_STORE_CONFIG).source,
        defaultErrorHandler = injectDefaultErrorHandler(),
        events = inject(Events),
        dispatch = injectDispatch(messagePhotoEvents),
      ) => ({
        unlock$: events.on(messagePhotoEvents.unlock).pipe(
          map(({ payload }) => payload),
          groupBy(({ message }) => message.timestamp),
          mergeMap(group =>
            group.pipe(
              exhaustMap(({ message, profile_id }) =>
                conversationService.unlockMessagePhoto(message.timestamp, source).pipe(
                  tap(response => creditsStore.onCreditsSpent(response)),
                  nonCancelableRequest(),
                  tapResponse({
                    next: response => dispatch.unlockSuccess({ response }),
                    error: error => dispatch.unlockFailure({ error, profile_id }),
                    finalize: () => dispatch.unlockEnd({ messageTimestamp: message.timestamp }),
                  }),
                  takeUntil(events.on(conversationEvents.select)),
                ),
              ),
            ),
          ),
        ),

        onUnlockSuccess$: events.on(messagePhotoEvents.unlockSuccess).pipe(
          map(({ payload }) => payload),
          map(({ response }) => messagePhotoEvents.view({ message: response.message })),
        ),

        onUnlockFailure$: events.on(messagePhotoEvents.unlockFailure).pipe(
          map(({ payload }) => payload),
          tap(({ error, profile_id }) => {
            if (isPaymentRequired(error)) {
              return creditsNavigationService.visitCredits('unlockMessagePhoto', { source, profile_id })
            }

            defaultErrorHandler(error)
          }),
        ),

        view$: events.on(messagePhotoEvents.view).pipe(
          map(({ payload }) => payload),
          tap(({ message }) => conversationUIService.openMessagePhotoGallery(message, injector)),
          filter(({ message }) => !message.is_me),
          groupBy(({ message }) => message.timestamp),
          mergeMap(group =>
            group.pipe(
              switchMap(({ message }) =>
                conversationService.open(message.timestamp, source).pipe(
                  catchError(error => {
                    defaultErrorHandler(error)
                    return EMPTY
                  }),
                ),
              ),
            ),
          ),
        ),
      }),
    ),
  )
